// src/components/SagittalViewTab.js
import React from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Card,
    CardContent,
    Grid,
    IconButton
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useFormContext } from '../context/FormContext';
import { fileToBase64 } from '../utils/fileUtils';
import { sagittalViewComments } from './SagittalViewConstants';

const SagittalViewTab = () => {
    const { state, actions } = useFormContext();
    const sagittalViewData = state.sagittalViewData || { image: null, mainComment: '', mandibleComment: '' };

    const handleImageChange = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/png, image/jpeg';
        input.onchange = async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;

            try {
                const base64 = await fileToBase64(file);
                actions.setSagittalViewData({
                    ...sagittalViewData,
                    image: {
                        base64,
                        name: file.name,
                        type: file.type
                    }
                });
            } catch (error) {
                console.error("Error during image processing:", error);
                actions.setError("Failed to load image. Please try a different file.");
            }
        };
        input.click();
    };

    const handleCommentChange = (event) => {
        actions.setSagittalViewData({
            ...sagittalViewData,
            mainComment: event.target.value
        });
    };

    const handleMandibleCommentChange = (event) => {
        actions.setSagittalViewData({
            ...sagittalViewData,
            mandibleComment: event.target.value
        });
    };

    const handleRemoveImage = () => {
        actions.setSagittalViewData({ ...sagittalViewData, image: null });
    };

    const isMandible = state.reportType.includes('Mandible');

    return (
        // CHANGED: The Card now acts as a flex container to control the overall height.
        // The height is calculated based on the viewport height (vh).
        // '200px' is an estimate for navbars, headers, etc. Adjust this value for your app's layout.
        <Card variant="outlined" sx={{ mb: 3 }}>
            {/* CHANGED: CardContent is also a flex container and will grow to fill the Card. */}
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    Sagittal View
                </Typography>

                {/* The Grid container now only handles its direct children's spacing and layout */}
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" gutterBottom>
                            Sagittal View Image
                        </Typography>
                        <Button variant="contained" onClick={handleImageChange} sx={{ mb: 1 }}>
                            {sagittalViewData.image ? 'Replace Image' : 'Upload Image'}
                        </Button>
                        
                        {sagittalViewData.image && (
                            <Box sx={{ mt: 2, mb: 3, position: 'relative', maxWidth: '100%' }}>
                                <Typography variant="caption" display="block" color="success.main">
                                    {sagittalViewData.image.name}
                                </Typography>
                                <img
                                    src={sagittalViewData.image.base64}
                                    alt="Sagittal View Preview"
                                    style={{ maxWidth: '100%', height: 'auto', marginTop: '10px', border: '1px solid #ddd' }}
                                />
                                <IconButton
                                    size="small"
                                    color="error"
                                    onClick={handleRemoveImage}
                                    sx={{ position: 'absolute', top: 0, right: 0, backgroundColor: 'white', '&:hover': {backgroundColor: 'rgba(255,255,255,0.8)'} }}
                                    title="Remove Image"
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        )}
                    </Grid>
                </Grid>
                
                <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                    Sagittal View Comments
                </Typography>
                
                {/* ADDED: A Box to act as a flex container for the TextField(s) to grow within. */}
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <TextField
                        label="Main Comment"
                        fullWidth
                        multiline
                        rows={2}
                        margin="normal"
                        value={sagittalViewData.mainComment || sagittalViewComments.main}
                        onChange={handleCommentChange}
                    />

                    {isMandible && (
                        <TextField
                            label="Mandible Specific Comment"
                            fullWidth
                            multiline
                            rows={2}
                            margin="normal"
                            value={sagittalViewData.mandibleComment || sagittalViewComments.mandible}
                            onChange={handleMandibleCommentChange}
                        />
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};

export default SagittalViewTab;